// src/shared/iapUserId.js
// A tiny, safe helper to create & persist a stable non-PII user id for IAP.

const STORAGE_KEY = 'iapUserId'

function hasWindow() {
	return typeof window !== 'undefined'
}
function hasLocalStorage() {
	try {
		return hasWindow() && !!window.localStorage
	} catch {
		return false
	}
}

function generateId() {
	try {
		if (
			hasWindow() &&
			window.crypto &&
			typeof window.crypto.randomUUID === 'function'
		) {
			return 'usr_' + window.crypto.randomUUID()
		}
	} catch {}
	return (
		'usr_' + Math.random().toString(36).slice(2) + '_' + Date.now().toString(36)
	)
}

/** Returns the current id or creates one, persists it, and returns it. */
export function getOrCreateIapUserId() {
	if (!hasLocalStorage()) {
		if (!hasWindow()) return generateId()
		if (!window.__IAP_VOLATILE_ID__) window.__IAP_VOLATILE_ID__ = generateId()
		return window.__IAP_VOLATILE_ID__
	}
	let id = window.localStorage.getItem(STORAGE_KEY)
	if (!id) {
		id = generateId()
		try {
			window.localStorage.setItem(STORAGE_KEY, id)
		} catch {}
	}
	return id
}

/** Read the current id without creating one. */
export function getIapUserId() {
	if (hasLocalStorage()) return window.localStorage.getItem(STORAGE_KEY)
	return hasWindow() ? window.__IAP_VOLATILE_ID__ || null : null
}

/** Force-set the id (e.g., to your app’s own user id). */
export function setIapUserId(id) {
	if (typeof id !== 'string' || !id.trim())
		throw new Error('setIapUserId: non-empty string required')
	const normalized = id.trim()
	if (hasLocalStorage()) {
		try {
			window.localStorage.setItem(STORAGE_KEY, normalized)
		} catch {}
	}
	if (hasWindow()) window.__IAP_VOLATILE_ID__ = normalized
	return normalized
}

/** Delete the stored id (useful for debugging). */
export function resetIapUserId() {
	if (hasLocalStorage()) {
		try {
			window.localStorage.removeItem(STORAGE_KEY)
		} catch {}
	}
	if (hasWindow()) delete window.__IAP_VOLATILE_ID__
}
